import { SubmitHandler } from "react-hook-form";
import { useServiceWizardForm } from "./useServiceWizardForm";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageMode } from "../../../../../common/types";
import { useAppContext } from "../../../../../AppContext";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { IEventWizardInitialData } from "../../models/eventWizardInitialDataModel";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import {
  getDefaultRoutePath,
  routePaths,
} from "../../../../../common/infrastructure/routes/routePaths";
import { useAddAdditionalLayoutButton } from "../../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { IconType } from "../../../../../common/icons/Icon";
import { PaymentTypes } from "../../../../../common/enums";
import { toZonedTime } from "date-fns-tz";
import { IServiceWizardFormModel } from "../models/serviceWizardFormModel";
import { useMeetingLinkRecipientsWarning } from "../../../common/hooks/useMeetingLinkRecipientsWarning";

interface IUseServiceWizardPageProps {
  pageMode: PageMode;
  initialData?: IEventWizardInitialData;
  studioEmployees?: ISelectFieldModel[];
}

export const useServiceWizardPage = ({
  pageMode,
  initialData,
  studioEmployees,
}: IUseServiceWizardPageProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { day, month, year } = useParams();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const { currentUser } = useAppContext();
  const { mutate } = useApiMutation();
  const { warnClientsWithoutEmail } = useMeetingLinkRecipientsWarning();

  useEffect(() => {
    if (year && month && day) {
      const date = new Date(+year, +month - 1, +day, 11);
      setSelectedDate(date);
    }
  }, [year, month, day]);

  const form = useServiceWizardForm(
    selectedDate,
    studioEmployees,
    initialData,
    pageMode
  );

  // const deleteServiceConfirm = () => {
  //   showCustomConfirmDialog(
  //     "Наистина ли искате да изтриете тази услуга?",
  //     deleteService,
  //     () => {},
  //     "warning",
  //     "Да",
  //     "Не"
  //   );
  // };

  // const deleteService = () => {
  //   mutate({
  //     endpoint: endpointsMetadata.planner.deleteEvent, // Will need to be updated for services
  //     method: "delete",
  //     invalidateKeys: [[endpointsMetadata.planner.getAll]],
  //     additionalParams: `itemId=${id}`,
  //   });
  //   navigate(getDefaultRoutePath(currentUser));
  // };

  // useAddAdditionalLayoutButton({
  //   iconType: IconType.DeleteIcon,
  //   buttonOnClick: deleteServiceConfirm,
  //   validator: id !== undefined,
  // });

  const successMessage = `Резервацията беше ${
    id ? "запазена" : "добавена"
  } успешно.`;

  const pageTitle = `${
    pageMode === "add"
      ? "Добавяне"
      : pageMode === "edit"
      ? "Редактиране"
      : "Клониране"
  } на резервация`;

  const buttonTitle =
    pageMode === "add"
      ? "Добавяне"
      : pageMode === "edit"
      ? "Редактиране"
      : "Клониране";

  const showEmailConfirmIfNeeded = (
    data: IServiceWizardFormModel,
    applyToFuture: boolean
  ) => {
    const originalTime = initialData?.currentlySelectedEvent?.startTime
      ? new Date(initialData.currentlySelectedEvent.startTime).getTime()
      : null;
    const newTime = data.date?.getTime();
    const dateChanged =
      originalTime !== null &&
      newTime !== undefined &&
      originalTime !== newTime;

    if (
      pageMode === "edit" &&
      dateChanged &&
      initialData?.currentlySelectedEvent?.consentedToAutomaticEmails
    ) {
      showCustomConfirmDialog(
        "Часът на резервацията беше променен. Искате ли да изпратите имейл за промяна на потребителя?",
        () => onSave(data, applyToFuture, true),
        () => onSave(data, applyToFuture, false),
        "warning",
        "Да",
        "Не"
      );
    } else {
      onSave(data, applyToFuture, false);
    }
  };

  const onTrySave: SubmitHandler<IServiceWizardFormModel> = async (data) => {
    if (
      pageMode === "edit" &&
      initialData?.currentlySelectedEvent?.isRecurring
    ) {
      showCustomConfirmDialog(
        "Искате ли да приложите промяната за всички бъдещи услуги от поредицата? Имайте предвид, че всички услуги, които са променени, няма да бъдат засегнати.",
        () => showEmailConfirmIfNeeded(data, true),
        () => showEmailConfirmIfNeeded(data, false),
        "warning",
        "Да",
        "Не"
      );
    } else {
      showEmailConfirmIfNeeded(data, false);
    }
  };

  const onSave = async (
    data: IServiceWizardFormModel,
    applyToFuture: boolean,
    sendUpdateEmail: boolean = false
  ) => {
    data.applyChangeToAllFutureEvents = applyToFuture;
    data.sendUpdateEmail = sendUpdateEmail;

    if (data.date && initialData?.studioSettings) {
      const offsetMinutes = toZonedTime(
        data.date,
        initialData.studioSettings.timezone
      ).getTimezoneOffset();

      const adjustedDate = new Date(
        data.date.getTime() - offsetMinutes * 60 * 1000
      );

      data.date = adjustedDate;
    }

    mutate(
      {
        endpoint: endpointsMetadata.planner.serviceWizard,
        globalIsLoading: true,
        method: "post",
        invalidateKeys: [
          [endpointsMetadata.planner.getAll],
          [endpointsMetadata.planner.getEvent],
          [endpointsMetadata.planner.getSignUpEventData],
        ],
        successMessage: successMessage,
        ...data,
      },
      {
        onSuccess: (savedEventId) => {
          // A remote service emails its client the Meet link; tell the staff member when
          // that client has no email. A new reservation always has a client, an edit only
          // matters when another client was put on it.
          const isRemoteService = !!initialData?.services?.find(
            (x) => x.id === data.serviceId
          )?.isRemote;
          const clientChanged =
            data.userId !== initialData?.currentlySelectedEvent?.individualUserId;
          const serviceChanged =
            data.serviceId !== initialData?.currentlySelectedEvent?.serviceId;

          if (
            isRemoteService &&
            !!data.userId &&
            (pageMode !== "edit" || clientChanged || serviceChanged)
          ) {
            warnClientsWithoutEmail(Number(savedEventId), [data.userId]);
          }

          if (applyToFuture) {
            navigate(routePaths.calendar);
            return;
          }
          navigate(routePaths.calendar);
        },
        onError: (error) => {},
      }
    );
  };

  const date = form.watch("date");

  // The selected service keeps the employee busy for its buffer after each reservation,
  // which the availability checks count as busy time.
  const serviceId = form.watch("serviceId");
  const bufferAfterMinutes =
    initialData?.services?.find((service) => service.id === serviceId)
      ?.bufferAfterMinutes ?? 0;

  const dateText = useMemo(() => {
    return (
      !!date &&
      `Дата: ${String(date?.getDate()).padStart(2, "0")}/${String(
        date?.getMonth() + 1
      ).padStart(2, "0")}/${date?.getFullYear()}`
    );
  }, [date]);

  const tenantSupportsMultisport =
    !!initialData?.studioSettings?.paymentTypes.find(
      (x) => x.paymentType === PaymentTypes.Multisport
    );

  const tenantSupportsCoolFit =
    !!initialData?.studioSettings?.paymentTypes.find(
      (x) => x.paymentType === PaymentTypes.CoolFit
    );

  return {
    form,
    pageTitle,
    buttonTitle,
    dateText,
    initialData,
    studioEmployees,
    enableMultisport: tenantSupportsMultisport,
    enableCoolFit: tenantSupportsCoolFit,
    bufferAfterMinutes,
    onTrySave,
  };
};
